import { lazy } from 'react';

// project import
import DashboardLayout from 'layout/Dashboard';
import Loadable from 'components/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import RightsGuard from 'utils/route-guard/RightsGuard';
import { Rights } from '../graphql/types';

// pages routing
const NotFoundError = Loadable(lazy(() => import('pages/maintenance/404')));
const UnexpectedError = Loadable(lazy(() => import('pages/maintenance/500')));
Loadable(lazy(() => import('pages/maintenance/under-construction')));
Loadable(lazy(() => import('pages/maintenance/coming-soon')));
// render - merchants pages
const LendersPage = Loadable(lazy(() => import('pages/merchants/lenders-list')));
const CompaniesPage = Loadable(lazy(() => import('pages/merchants/companies-list')));
const AllCompaniesPage = Loadable(lazy(() => import('pages/merchants/all-companies-list')));
const CashAdvancesPage = Loadable(lazy(() => import('pages/merchants/mca-list')));
const CashAdvancesProfilePage = Loadable(lazy(() => import('pages/merchants/mca-profile')));
// render - settings pages
const IntegrationsPage = Loadable(lazy(() => import('pages/settings/integrations')));
const ExternalServiceConfigurationsPage = Loadable(lazy(() => import('pages/settings/external-service-configurations')));
const ConfigFeesPage = Loadable(lazy(() => import('pages/settings/config.fees')));
const UsersPage = Loadable(lazy(() => import('pages/settings/users-list')));
const PermissionsPage = Loadable(lazy(() => import('pages/settings/permissions-list')));
// render - account profile
const AccountProfile = Loadable(lazy(() => import('pages/profiles/account')));
const AccountTabProfile = Loadable(lazy(() => import('sections/profiles/account/TabProfile')));
const AccountTabPersonal = Loadable(lazy(() => import('sections/profiles/account/TabEditPersonalDetails')));
const AccountTabPassword = Loadable(lazy(() => import('sections/profiles/account/TabPassword')));
const AccountTabPreferences = Loadable(lazy(() => import('sections/profiles/account/TabPreferences')));
// render - dashboard
const AdminDashboard = Loadable(lazy(() => import('pages/dashboard')));
const InvestorDashboard = Loadable(lazy(() => import('pages/dashboard/investor')));
const FunderDashboard = Loadable(lazy(() => import('pages/dashboard/funder')));
const PortfoliosDashboard = Loadable(lazy(() => import('pages/dashboard/portfolios')));
// render - portfolio
const Ledger = Loadable(lazy(() => import('pages/ledger/investor-balance-page')));
// render - transactions
const Transactions = Loadable(lazy(() => import('pages/transactions/transactions-list')));
// render - home
const McaDetailsBoardingPage = Loadable(lazy(() => import('pages/mca-profile-boarding-page')));
const ApplicationsBoardingPage = Loadable(lazy(() => import('pages/application-offer-boarding-page')));
const FundingPipelineBoardingPage = Loadable(lazy(() => import('pages/funding-pipeline-boarding-page')));
const InvestorsList = Loadable(lazy(() => import('pages/settings/investors-list')));
// render - caht
const GoToChatPage = Loadable(lazy(() => import('pages/chat/go-to-chat-page')));
// render - offers
const ApplicationProfilePage = Loadable(lazy(() => import('pages/offers/application-profile')));
const ApplicationOfferProfile = Loadable(lazy(() => import('pages/offers/application-offer-profile')));
const RehabOfferProfile = Loadable(lazy(() => import('pages/offers/application-rehab-offer-profile')));
// render - audit
const AuditLogsPage = Loadable(lazy(() => import('pages/audit/audit-list')));
// render - banks
const BanksListPage = Loadable(lazy(() => import('pages/banks/banks-list')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: 'dashboard/admin',
          element: <AdminDashboard />
        },
        {
          path: 'dashboard/investor',
          element: <InvestorDashboard />
        },
        {
          path: 'dashboard/investor/:id',
          element: <InvestorDashboard />
        },
        {
          path: 'dashboard/funder',
          element: <FunderDashboard />
        },
        {
          path: 'dashboard/funder/:id',
          element: <FunderDashboard />
        },
        {
          path: 'my-portfolio',
          element: <McaDetailsBoardingPage />
        },
        {
          path: 'portfolios',
          element: <McaDetailsBoardingPage />
        },
        {
          path: 'dashboard/portfolios',
          element: <PortfoliosDashboard />
        },
        {
          path: 'portfolio/:id',
          element: <McaDetailsBoardingPage />
        },
        {
          path: 'ledger/:id',
          element: <Ledger />
        },
        {
          path: 'my-ledger',
          element: <Ledger />
        },
        {
          path: 'transactions',
          element: <Transactions />
        },
        {
          path: 'applications',
          element: (
            <RightsGuard forbiddenRights={[Rights.IsBroker]} redirectTo="/funding-pipeline">
              <ApplicationsBoardingPage />
            </RightsGuard>
          )
        },
        {
          path: 'offers',
          element: (
            <RightsGuard forbiddenRights={[Rights.IsBroker]} redirectTo="/funding-pipeline">
              <ApplicationsBoardingPage />
            </RightsGuard>
          )
        },
        {
          path: 'funding-pipeline',
          element: <FundingPipelineBoardingPage />
        },
        {
          path: 'applications/:id',
          element: <ApplicationProfilePage />
        },
        {
          path: 'application/:id',
          element: <ApplicationProfilePage />
        },
        /*  {
          path: 'application/:applicationId/offer/:id',
          element: <ApplicationOfferProfile />
        },*/
        {
          path: 'application/:applicationId/rehab-offer/:id',
          element: <RehabOfferProfile />
        },
        {
          path: 'investors-list',
          element: <InvestorsList />
        },
        {
          path: 'portfolio/:investorId/mca/:id',
          element: <CashAdvancesProfilePage />
        },
        {
          path: 'profiles',
          children: [
            {
              path: 'account',
              element: <AccountProfile />,
              children: [
                {
                  path: 'basic',
                  element: <AccountTabProfile />
                },
                {
                  path: 'personal',
                  element: <AccountTabPersonal />
                },
                {
                  path: 'password',
                  element: <AccountTabPassword />
                },
                {
                  path: 'preferences',
                  element: <AccountTabPreferences />
                }
              ]
            }
          ]
        },
        {
          path: 'merchants',
          children: [
            {
              path: 'list',
              element: <AllCompaniesPage />
            },
            {
              path: 'mcas',
              element: (
                <RightsGuard forbiddenRights={[Rights.IsBroker]} redirectTo="/funding-pipeline">
                  <McaDetailsBoardingPage />
                </RightsGuard>
              )
            },
            {
              path: 'mcas/:useScroll',
              element: (
                <RightsGuard forbiddenRights={[Rights.IsBroker]} redirectTo="/funding-pipeline">
                  <CashAdvancesPage />
                </RightsGuard>
              )
            },
            {
              path: 'mca/:id',
              element: (
                <RightsGuard forbiddenRights={[Rights.IsBroker]} redirectTo="/funding-pipeline">
                  <CashAdvancesProfilePage />
                </RightsGuard>
              )
            }
          ]
        },
        {
          path: 'underwriters',
          children: [
            {
              path: 'list',
              element: <LendersPage />
            }
          ]
        },
        {
          path: 'funding-companies',
          children: [
            {
              path: 'list',
              element: <CompaniesPage />
            }
          ]
        },
        {
          path: 'funding-companies',
          children: [
            {
              path: 'list',
              element: <LendersPage />
            }
          ]
        },
        {
          path: 'settings',
          children: [
            {
              path: 'integrations',
              element: <IntegrationsPage />
            },
            {
              path: 'external-service-configurations',
              element: <ExternalServiceConfigurationsPage />
            },
            {
              path: 'users',
              element: <UsersPage />
            },
            {
              path: 'fees',
              element: <ConfigFeesPage />
            },
            {
              path: 'permissions',
              element: <PermissionsPage />
            }
          ]
        },
        {
          path: 'go-to-chat/room',
          element: <GoToChatPage />
        },
        {
          path: 'banks',
          children: [
            {
              path: 'list',
              element: <BanksListPage />
            }
          ]
        },
        {
          path: 'audit-logs',
          element: <AuditLogsPage />
        },
        {
          path: '*',
          element: <NotFoundError />
        },
        {
          path: 'error',
          element: <UnexpectedError />
        }
      ]
    }
  ]
};

export default MainRoutes;
